import { Input, Modal, notification } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import {
  carboneTypeOptions,
  ModalSizeValue,
  isDefaultOptions,
} from "shared/constants";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { ReportItem, useUpdateReportMutation } from "shared/api";
import { handleRequestError, renderReportType } from "shared/helpers";
import { Colors } from "shared/themes";
import { FormInput, FormSelect } from "shared/ui";

interface EditTemplateConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  formattedReportCategoriesOptions:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

const EditTemplateConfigModal: FC<EditTemplateConfigModalProps> = ({
  isOpen,
  onClose,
  formattedReportCategoriesOptions,
}) => {
  const { t } = useTranslation();

  const [api, contextHolder] = notification.useNotification();

  const {
    resetForm,
    values,
    handleChange,
    handleBlur,
    errors,
    isValid,
    setFieldValue,
  } = useFormikContext<ReportItem>();

  const closeModal = () => {
    onClose();
    resetForm();
  };

  const [updateReport] = useUpdateReportMutation();

  const handleReportUpdate = async () => {
    try {
      await updateReport({
        ...values,
        report_category: values.report_category.id,
      }).unwrap();

      api.success({
        message: t("reports.success-title"),
        description: t("reports.success-update-msg"),
      });
    } catch (error) {
      api.error({
        message: t("reports.error-title"),
        description: handleRequestError(error, t("reports.error")),
      });
    } finally {
      closeModal();
    }
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={t("reports.update-report")}
        open={isOpen}
        onCancel={closeModal}
        width={ModalSizeValue.XL_MEDIUM}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${Colors.blue.primary}`,
          },
        }}
        onOk={handleReportUpdate}
      >
        <Title level={5} className="mb-1.25">
          {t("reports.name")}
        </Title>

        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t("reports.name")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name}
          isError={!!errors.name}
        />

        <Title level={5} className="mb-1.25">
          {t("reports.template-id")}
        </Title>

        <FormInput
          value={values.template_id}
          fieldName="template_id"
          placeholder={t("reports.template-id")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.template_id}
          isError={!!errors.template_id}
        />

        <Title level={5} className="mb-1.25">
          {t("reports.report-type")}
        </Title>

        <Input size="large" disabled value={renderReportType(values)} />

        <Title level={5} className="mb-1.25">
          {t("reports.report-category")}
        </Title>

        <FormSelect
          value={values.report_category?.id ?? null}
          placeholder={t("reports.report-category")}
          fieldName="report_category"
          options={formattedReportCategoriesOptions}
          setFieldValue={(field, value) => {
            const selected = formattedReportCategoriesOptions?.find(
              (opt) => opt.value === value
            );
            setFieldValue(
              field,
              selected ? { id: selected.value, name: selected.label } : null
            );
          }}
          handleBlur={handleBlur}
        />

        <Title level={5} className="mb-1.25">
          {t("reports.carbone-type")}
        </Title>

        <FormSelect
          value={values.carbone_type ?? null}
          placeholder={t("reports.carbone-type")}
          fieldName="carbone_type"
          options={carboneTypeOptions}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
        />

        <Title level={5} className="mb-1.25">
          {t("reports.is-default")}
        </Title>

        <FormSelect
          value={values.is_default}
          placeholder={t("reports.is-default")}
          fieldName="is_default"
          options={isDefaultOptions}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
        />
      </Modal>
    </>
  );
};

export { EditTemplateConfigModal };
